/**
 * Function problems, the value format - `EX-FNC04`, Wave 36 call 5, `docs/27`
 * §9.41 (the engine is `EX-FNC03`, ADR-0188).
 *
 * A function problem's tests stay text files. The input holds one JSON-like
 * value per line, one line per parameter per call, and calls repeat; the answer
 * file holds one value per call:
 *
 * ```
 * [7,1,5,3,6,4]      <- call 1, prices
 * [7,6,4,3,1]        <- call 2, prices
 * ```
 *
 * **This is a port of the server's reader, not a guess at it**
 * (`progsity-backend/src/modules/judge/services/function-value.ts`). The
 * setter's test editor and the student's run panel read a value as the judge
 * will, and a badge that says "fine" to a line the judge refuses would be
 * worse than no badge. The test file runs the server's own vectors
 * (`function-value.vectors.json`, copied byte for byte), vector for vector, so
 * a change on either side shows up as a red test here.
 *
 * The one difference is the refusal: the server answers an English sentence,
 * this answers a code and what it names, so the page can say it in Bangla.
 *
 * Pure, no I/O.
 */
import { elementTypeOf, type FunctionSignature, type FunctionValueType } from "./function-signature";

/** A value as the judge holds it: a number for int, long and double, a string for char and string. */
export type FunctionValue = number | boolean | string | FunctionValue[];

/** What a refused line was instead, for the sentence that says so. */
export type ValueFound = { kind: "number"; text: string } | { kind: "string" } | { kind: "bool"; text: string } | { kind: "list" };

export type ValueRefusal =
  | { code: "too_deep" }
  | { code: "ended" }
  | { code: "not_a_number"; character: string }
  | { code: "null" }
  | { code: "not_a_value"; character: string }
  | { code: "list_unclosed" }
  | { code: "list_comma"; character: string }
  | { code: "string_unclosed" }
  | { code: "control_character" }
  | { code: "short_escape" }
  | { code: "bad_escape"; escape: string }
  | { code: "nul" }
  | { code: "surrogate" }
  | { code: "trailing"; rest: string }
  | { code: "not_list"; type: FunctionValueType; found: ValueFound }
  | { code: "not_whole"; type: "int" | "long"; found: ValueFound }
  | { code: "int_range"; text: string }
  | { code: "long_range"; text: string }
  | { code: "not_double"; found: ValueFound }
  | { code: "double_range"; text: string }
  | { code: "not_bool"; found: ValueFound }
  | { code: "not_char"; found: ValueFound }
  | { code: "char_one" }
  | { code: "not_string"; found: ValueFound };

export type ValueReading = { ok: true; value: FunctionValue } | { ok: false; refusal: ValueRefusal };

const INT_MIN = -2147483648;
const INT_MAX = 2147483647;
/** JavaScript holds an integer exactly only to 2^53 - 1, and every function problem offers JavaScript. */
export const LONG_ABS_MAX = Number.MAX_SAFE_INTEGER;
const INTEGER_PATTERN = /^-?(?:0|[1-9][0-9]*)$/;
const NUMBER_PATTERN = /-?(?:0|[1-9][0-9]*)(?:\.[0-9]+)?(?:[eE][+-]?[0-9]+)?/y;
const HEX4_PATTERN = /^[0-9a-fA-F]{4}$/;
/** Deeper than any type here (a grid is 2), so a long line cannot recurse far. */
const MAX_DEPTH = 3;

const QUOTE = '"';
const BACKSLASH = String.fromCharCode(92);
/** The one-letter escapes a JSON string may hold, and what each stands for. */
const ESCAPES: Readonly<Record<string, string>> = {
  [QUOTE]: QUOTE,
  [BACKSLASH]: BACKSLASH,
  "/": "/",
  b: String.fromCharCode(8),
  f: String.fromCharCode(12),
  n: String.fromCharCode(10),
  r: String.fromCharCode(13),
  t: String.fromCharCode(9),
};

type Node =
  | { kind: "number"; text: string }
  | { kind: "string"; value: string }
  | { kind: "bool"; value: boolean }
  | { kind: "array"; items: Node[] };

class Refused extends Error {
  constructor(readonly refusal: ValueRefusal) {
    super(refusal.code);
  }
}

function refuse(refusal: ValueRefusal): never {
  throw new Refused(refusal);
}

class Reader {
  pos = 0;
  constructor(readonly text: string) {}

  skipSpace(): void {
    while (this.pos < this.text.length && (this.text[this.pos] === " " || this.text[this.pos] === "\t")) this.pos += 1;
  }

  node(depth: number): Node {
    if (depth > MAX_DEPTH) refuse({ code: "too_deep" });
    this.skipSpace();
    const ch = this.text[this.pos];
    if (ch === undefined) refuse({ code: "ended" });
    if (ch === "[") return this.array(depth);
    if (ch === QUOTE) return { kind: "string", value: this.string() };
    if (this.text.startsWith("true", this.pos)) {
      this.pos += 4;
      return { kind: "bool", value: true };
    }
    if (this.text.startsWith("false", this.pos)) {
      this.pos += 5;
      return { kind: "bool", value: false };
    }
    if (ch === "-" || (ch >= "0" && ch <= "9")) {
      NUMBER_PATTERN.lastIndex = this.pos;
      const match = NUMBER_PATTERN.exec(this.text);
      if (!match) refuse({ code: "not_a_number", character: ch });
      this.pos += match[0].length;
      return { kind: "number", text: match[0] };
    }
    if (this.text.startsWith("null", this.pos)) refuse({ code: "null" });
    return refuse({ code: "not_a_value", character: ch });
  }

  array(depth: number): Node {
    this.pos += 1;
    const items: Node[] = [];
    this.skipSpace();
    if (this.text[this.pos] === "]") {
      this.pos += 1;
      return { kind: "array", items };
    }
    for (;;) {
      items.push(this.node(depth + 1));
      this.skipSpace();
      const ch = this.text[this.pos];
      if (ch === ",") {
        this.pos += 1;
        continue;
      }
      if (ch === "]") {
        this.pos += 1;
        return { kind: "array", items };
      }
      refuse(ch === undefined ? { code: "list_unclosed" } : { code: "list_comma", character: ch });
    }
  }

  string(): string {
    this.pos += 1;
    let out = "";
    for (;;) {
      const ch = this.text[this.pos];
      if (ch === undefined) refuse({ code: "string_unclosed" });
      const code = ch.charCodeAt(0);
      this.pos += 1;
      if (ch === QUOTE) break;
      if (code < 0x20) refuse({ code: "control_character" });
      if (ch !== BACKSLASH) {
        out += ch;
        continue;
      }
      const escape = this.text[this.pos];
      this.pos += 1;
      if (escape === "u") {
        const hex = this.text.slice(this.pos, this.pos + 4);
        if (!HEX4_PATTERN.test(hex)) refuse({ code: "short_escape" });
        this.pos += 4;
        out += String.fromCharCode(parseInt(hex, 16));
        continue;
      }
      const meant = escape === undefined ? undefined : ESCAPES[escape];
      if (meant === undefined) refuse({ code: "bad_escape", escape: escape ?? "" });
      out += meant;
    }
    for (let index = 0; index < out.length; index += 1) {
      const unit = out.charCodeAt(index);
      if (unit === 0) refuse({ code: "nul" });
      if (unit >= 0xd800 && unit <= 0xdbff) {
        const next = out.charCodeAt(index + 1);
        if (!(next >= 0xdc00 && next <= 0xdfff)) refuse({ code: "surrogate" });
        index += 1;
      } else if (unit >= 0xdc00 && unit <= 0xdfff) {
        refuse({ code: "surrogate" });
      }
    }
    return out;
  }
}

function found(node: Node): ValueFound {
  switch (node.kind) {
    case "number":
      return { kind: "number", text: node.text };
    case "string":
      return { kind: "string" };
    case "bool":
      return { kind: "bool", text: node.value ? "true" : "false" };
    case "array":
      return { kind: "list" };
  }
}

function typed(node: Node, type: FunctionValueType): FunctionValue {
  const element = elementTypeOf(type);
  if (element) {
    if (node.kind !== "array") refuse({ code: "not_list", type, found: found(node) });
    return node.items.map((item) => typed(item, element));
  }
  switch (type) {
    case "int":
    case "long": {
      if (node.kind !== "number" || !INTEGER_PATTERN.test(node.text)) refuse({ code: "not_whole", type, found: found(node) });
      const value = Number(node.text);
      if (type === "int" && (value < INT_MIN || value > INT_MAX)) refuse({ code: "int_range", text: node.text });
      if (type === "long" && !(Math.abs(value) <= LONG_ABS_MAX && Number.isSafeInteger(value))) {
        refuse({ code: "long_range", text: node.text });
      }
      return value === 0 ? 0 : value;
    }
    case "double": {
      if (node.kind !== "number") refuse({ code: "not_double", found: found(node) });
      const value = Number(node.text);
      if (!Number.isFinite(value)) refuse({ code: "double_range", text: node.text });
      return value === 0 ? 0 : value;
    }
    case "bool":
      if (node.kind !== "bool") refuse({ code: "not_bool", found: found(node) });
      return node.value;
    case "char": {
      if (node.kind !== "string") refuse({ code: "not_char", found: found(node) });
      const code = node.value.length === 1 ? node.value.charCodeAt(0) : -1;
      if (code < 0x20 || code > 0x7e) refuse({ code: "char_one" });
      return node.value;
    }
    case "string":
      if (node.kind !== "string") refuse({ code: "not_string", found: found(node) });
      return node.value;
    default:
      return refuse({ code: "not_a_value", character: "" });
  }
}

/** One line read as one value of `type`. */
export function parseValue(line: string, type: FunctionValueType): ValueReading {
  const reader = new Reader(line);
  try {
    const node = reader.node(0);
    reader.skipSpace();
    if (reader.pos < line.length) refuse({ code: "trailing", rest: line.slice(reader.pos, reader.pos + 20) });
    return { ok: true, value: typed(node, type) };
  } catch (err) {
    if (err instanceof Refused) return { ok: false, refusal: err.refusal };
    throw err;
  }
}

/**
 * The canonical text of a value: no spaces, JSON strings, a double in
 * JavaScript's shortest form that reads back to the same number. What the
 * setter's tools write and what a run shows.
 */
export function serializeValue(value: FunctionValue, type: FunctionValueType): string {
  const element = elementTypeOf(type);
  if (element) {
    const items = Array.isArray(value) ? value : [];
    return `[${items.map((item) => serializeValue(item, element)).join(",")}]`;
  }
  switch (type) {
    case "int":
    case "long":
    case "double":
      return String(value);
    case "bool":
      return value ? "true" : "false";
    default:
      return JSON.stringify(value);
  }
}

export interface CompareOptions {
  tolerance: number;
  unordered: boolean;
}

function doublesClose(expected: number, actual: number, tolerance: number): boolean {
  const difference = Math.abs(expected - actual);
  return difference <= tolerance || difference <= tolerance * Math.abs(expected);
}

function equalAt(expected: FunctionValue, actual: FunctionValue, type: FunctionValueType, tolerance: number): boolean {
  const element = elementTypeOf(type);
  if (element) {
    if (!Array.isArray(expected) || !Array.isArray(actual) || expected.length !== actual.length) return false;
    return expected.every((item, index) => {
      const other = actual[index];
      return other !== undefined && equalAt(item, other, element, tolerance);
    });
  }
  if (type === "double") {
    return typeof expected === "number" && typeof actual === "number" && doublesClose(expected, actual, tolerance);
  }
  return expected === actual;
}

function itemOrder(type: FunctionValueType): (a: FunctionValue, b: FunctionValue) => number {
  if (type === "double" || type === "int" || type === "long") {
    return (a, b) => (typeof a === "number" && typeof b === "number" ? a - b : 0);
  }
  return (a, b) => {
    const left = serializeValue(a, type);
    const right = serializeValue(b, type);
    return left < right ? -1 : left > right ? 1 : 0;
  };
}

/** Two values of one type, as the value checker compares them. */
export function valuesEqual(
  expected: FunctionValue,
  actual: FunctionValue,
  type: FunctionValueType,
  options: CompareOptions,
): boolean {
  const element = elementTypeOf(type);
  if (options.unordered && element) {
    if (!Array.isArray(expected) || !Array.isArray(actual)) return false;
    const order = itemOrder(element);
    return equalAt([...expected].sort(order), [...actual].sort(order), type, options.tolerance);
  }
  return equalAt(expected, actual, type, options.tolerance);
}

/** The lines that hold a value, each with its line number in the file. Blank lines skipped, a trailing CR dropped. */
export function valueLines(text: string): { line: number; text: string }[] {
  const lines: { line: number; text: string }[] = [];
  for (const [index, value] of text.split("\n").entries()) {
    const line = value.endsWith("\r") ? value.slice(0, -1) : value;
    if (/^[ \t]*$/.test(line)) continue;
    lines.push({ line: index + 1, text: line });
  }
  return lines;
}

/** A problem with a test file, named the way an author fixes it. */
export type FunctionTestIssue =
  | { stream: "input"; line: number; param: string; refusal: ValueRefusal }
  | { stream: "expected"; line: number; refusal: ValueRefusal }
  | { stream: "input"; code: "no_calls" }
  | { stream: "input"; code: "short_call"; param: string; width: number }
  | { stream: "expected"; code: "count"; calls: number; answers: number };

/**
 * A test's two files split into calls, each line read where it can be: what
 * the editor's fields show. A file that does not split evenly keeps its last,
 * short call with the missing lines empty, so the editor can show the hole.
 */
export function splitCalls(signature: FunctionSignature, input: string, expected: string): { params: string[]; answer: string }[] {
  const inputs = valueLines(input).map((entry) => entry.text.trim());
  const answers = valueLines(expected).map((entry) => entry.text.trim());
  const width = signature.params.length;
  const count = Math.max(Math.ceil(inputs.length / width), answers.length);
  const calls: { params: string[]; answer: string }[] = [];
  for (let call = 0; call < count; call += 1) {
    calls.push({
      params: signature.params.map((_, offset) => inputs[call * width + offset] ?? ""),
      answer: answers[call] ?? "",
    });
  }
  return calls;
}

/** Calls written back as the two files: one line per parameter per call, one answer per call. */
export function joinCalls(calls: readonly { params: readonly string[]; answer: string }[]): { input: string; expected: string } {
  const input = calls.flatMap((call) => call.params.map((value) => value.trim())).join("\n");
  const expected = calls.map((call) => call.answer.trim()).join("\n");
  return { input: input ? `${input}\n` : "", expected: expected ? `${expected}\n` : "" };
}

/** Why a test's two files do not fit the signature, or `null` when they do: the server's `checkFunctionTest`. */
export function checkFunctionTest(signature: FunctionSignature, input: string, expected: string): FunctionTestIssue | null {
  const lines = valueLines(input);
  const width = signature.params.length;
  if (lines.length === 0) return { stream: "input", code: "no_calls" };
  let calls = 0;
  for (let start = 0; start < lines.length; start += width) {
    for (const [offset, param] of signature.params.entries()) {
      const entry = lines[start + offset];
      if (!entry) return { stream: "input", code: "short_call", param: param.name, width };
      const reading = parseValue(entry.text, param.type);
      if (!reading.ok) return { stream: "input", line: entry.line, param: param.name, refusal: reading.refusal };
    }
    calls += 1;
  }
  let answers = 0;
  for (const entry of valueLines(expected)) {
    const reading = parseValue(entry.text, signature.returns);
    if (!reading.ok) return { stream: "expected", line: entry.line, refusal: reading.refusal };
    answers += 1;
  }
  if (answers !== calls) return { stream: "expected", code: "count", calls, answers };
  return null;
}
